import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import { useGetAllSystemDetailCodesDropdown } from "../../hooks/common/query";
import { mapToOptions } from "../../lib/functions";
import { Button } from "../button";
import { SYSTEM_DETAIL_CODES } from "../../constants/common";
import debounce from "lodash.debounce";
import { SearchBlacklistVisitorsCardSchema } from "../../schemas/searchBlacklistVisitorsCardSchema";
import { useGetAllBlacklistedVisitors } from "../../hooks/visitor/query";

interface SearchBlacklistVisitorsCardProps {
  setAllVisitors: Dispatch<SetStateAction<never[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  pageSize: number;
}

const initialState = {
  blacklistType: "",
};

const SearchBlacklistVisitorsCard = ({
  setAllVisitors,
  page,
  setPages,
  search,
  setCount,
  pageSize,
}: SearchBlacklistVisitorsCardProps) => {
  const {
    control,
    formState: { errors },
    watch,
    reset,
  } = useFormWithSchema(SearchBlacklistVisitorsCardSchema);

  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("");

  // const { data: allNationalities } = useGetAllNationalities();

  // const nationalities = useMemo(
  //   () => mapToOptions("name", "name_Ar", "iso3", allNationalities),
  //   [allNationalities]
  // );

  const { data: allBlacklistTypes } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.BLACKLIST_TYPES,
  });

  const blacklistTypes = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allBlacklistTypes),
    [allBlacklistTypes]
  );

  const eid = watch("eid");
  const passportNumber = watch("passportNumber");
  const iqama = watch("iqama");
  const visaNumber = watch("visaNumber");

  const [debouncedEID, setDebouncedEID] = useState(eid);

  const handleEIDChange = useCallback(
    debounce((value) => setDebouncedEID(value), 500),
    []
  );

  const [debouncedPassportNumber, setDebouncedPassportNumber] =
    useState(passportNumber);

  const handlePassportChange = useCallback(
    debounce((value) => setDebouncedPassportNumber(value), 500),
    []
  );

  const [debouncedIqama, setDebouncedIqama] = useState(iqama);

  const handleIqamaChange = useCallback(
    debounce((value) => setDebouncedIqama(value), 500),
    []
  );

  const [debouncedVisaNumber, setDebouncedVisaNumber] = useState(visaNumber);

  const handleVisaNumberChange = useCallback(
    debounce((value) => setDebouncedVisaNumber(value), 500),
    []
  );

  const blacklistType = watch("blacklistType");
  const companyName = watch("companyName");

  const [debouncedCompanyName, setDebouncedCompanyName] = useState(companyName);

  const handleCompanyNameChange = useCallback(
    debounce((value) => setDebouncedCompanyName(value), 500),
    []
  );

  useEffect(() => {
    handleEIDChange(eid);
    return () => handleEIDChange.cancel();
  }, [eid, handleEIDChange]);

  useEffect(() => {
    handlePassportChange(passportNumber);
    return () => handlePassportChange.cancel();
  }, [passportNumber, handlePassportChange]);

  useEffect(() => {
    handleIqamaChange(iqama);
    return () => handleIqamaChange.cancel();
  }, [iqama, handleIqamaChange]);

  useEffect(() => {
    handleVisaNumberChange(visaNumber);
    return () => handleVisaNumberChange.cancel();
  }, [visaNumber, handleVisaNumberChange]);

  useEffect(() => {
    handleCompanyNameChange(companyName);
    return () => handleCompanyNameChange.cancel();
  }, [companyName, handleCompanyNameChange]);

  const { data: blacklistedVisitors } = useGetAllBlacklistedVisitors({
    ...(blacklistType && { N_BlacklistType: Number(blacklistType) }),
    S_EmiratesID: debouncedEID,
    S_PassportNumber: debouncedPassportNumber,
    S_Iqama: debouncedIqama,
    S_VisaNo: debouncedVisaNumber,
    S_CompanyName: debouncedCompanyName,
    S_Nationality: watch("nationality"),
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  useEffect(() => {
    if (blacklistedVisitors) {
      setAllVisitors(blacklistedVisitors.data || []);
      setPages(blacklistedVisitors.pagination?.pages || 1);
      setCount(blacklistedVisitors.pagination?.count || 0);
    }
  }, [setAllVisitors, setPages, setCount, blacklistedVisitors]);

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2 lg:gap-4">
              {/* <Controller
                name="blacklistType"
                control={control}
                render={({ field: { onChange, name, value } }) => {
                  return (
                    <ComboboxInput
                      options={blacklistTypes}
                      value={
                        blacklistTypes?.find((item) => item.value === value)
                          ?.labelEn || ""
                      }
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      label={t("blacklistType")}
                    />
                  );
                }}
              /> */}

              <Controller
                name="eid"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("nationalID")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                  />
                )}
              />
              <Controller
                name="passportNumber"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("passportNumber")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                  />
                )}
              />

              <Controller
                name="iqama"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("iqama")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                  />
                )}
              />

              <Controller
                name="visaNumber"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("visaNumber")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                  />
                )}
              />

              {/* <Controller
                name="companyName"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("companyName")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                  />
                )}
              /> */}

              {/* <Controller
                name="nationality"
                control={control}
                render={({ field: { name, onChange, value } }) => {
                  return (
                    <ComboboxInput
                      label={t("nationality")}
                      options={nationalities}
                      onChange={onChange}
                      value={
                        nationalities?.find((item) => item.value === value)
                          ?.labelEn || ""
                      }
                      error={errors?.[name] && t("nationalityRequired")}
                    />
                  );
                }}
              /> */}
            </div>
          </form>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent dark:!ring-white dark:text-white"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default React.memo(SearchBlacklistVisitorsCard);
